import type { Session } from '../api/client';
import { displayDate, weekdayLabel } from '../api/adapter';
import { accentFor } from '../components/calendar/CalendarChip';
import type { WireCalendarEvent, WireCalendarPerson } from '../api/wire';

import { CalendarChip } from '../components/calendar/CalendarChip.jsx';
import { FieldRow } from '../components/forms/FieldRow.jsx';
import { SectionHeading } from '../components/composed/SectionHeading';

/** RSVP, as a mark and a word — never colour alone, per R3.
 *
 *  The service normalises Google's values to
 *  `accepted | tentative | declined | needsAction | unknown`, or omits the field
 *  when the provider gave none. `unknown` is not in the reference's table; it is
 *  shown as itself rather than folded into `NO REPLY`, because "we could not
 *  read their answer" and "they have not answered" are different facts.
 */
const RSVP: Record<string, { mark: string; label: string; colour: string; dim: boolean }> = {
  accepted: { mark: '✓', label: 'ACCEPTED', colour: 'var(--bujo-state-done)', dim: false },
  tentative: { mark: '?', label: 'TENTATIVE', colour: 'var(--bujo-accent)', dim: false },
  declined: { mark: '✗', label: 'DECLINED', colour: 'var(--bujo-state-error)', dim: true },
  needsAction: { mark: '·', label: 'NO REPLY', colour: 'var(--bujo-text-meta)', dim: true },
  unknown: { mark: '·', label: 'UNKNOWN', colour: 'var(--bujo-text-meta)', dim: true },
};

const rsvpOf = (person: WireCalendarPerson) =>
  RSVP[person.response_status ?? 'needsAction'] ?? RSVP.needsAction!;

const nameOf = (person: WireCalendarPerson) =>
  person.display_name?.trim() || person.email?.trim() || 'unnamed';

/** A calendar event, read-only and saying so.
 *
 *  R9 is absolute: no create, edit, RSVP or delete exists anywhere in this tree.
 *  The screen states that in words rather than relying on the absence of
 *  buttons, because absence is not a message.
 */
export function EventDetail({
  event,
  asOf,
  freshness,
  onDismiss,
}: {
  session?: Session;
  event: WireCalendarEvent;
  /** When the calendar data was read, from the range response. */
  asOf?: string | null;
  freshness?: string | null;
  onDismiss: () => void;
}) {
  const attendees = event.attendees ?? [];
  const when = event.all_day
    ? 'All day'
    : `${clock(event.start)} – ${clock(event.end)}${event.time_zone ? ` · ${event.time_zone}` : ''}`;
  const date = event.start.slice(0, 10);

  return (
    <div style={sheet}>
      <header style={{ borderBottom: '1px solid var(--bujo-rule-hairline)', paddingBottom: 'var(--bujo-space-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={eyebrow}>BUJO / DAILY LOG / EVENT</span>
          <button type="button" onClick={onDismiss} style={closeButton}>
            CLOSE
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bujo-space-3)', paddingTop: 'var(--bujo-space-3)' }}>
          <CalendarChip name={event.calendar_name} accent={accentFor(event.calendar_name)} />
          {/* The chip already carries the monogram; repeating it read `WO WO`. */}
          <span style={meta}>{event.calendar_name.toUpperCase()} · READ-ONLY</span>
        </div>
        <h1 style={title}>{event.title}</h1>
      </header>

      <div style={{ overflowY: 'auto' }}>
        <FieldRow label="WHEN">
          {weekdayLabel(date)} {displayDate(date)}
          <div style={{ color: 'var(--bujo-text-dim)' }}>{when}</div>
        </FieldRow>
        {event.location ? <FieldRow label="WHERE">{event.location}</FieldRow> : null}
        {event.meeting_url ? (
          <FieldRow label="LINK">
            <span style={{ color: 'var(--bujo-label)', wordBreak: 'break-all' }}>{event.meeting_url}</span>
          </FieldRow>
        ) : null}
        {event.organizer ? <FieldRow label="ORGANIZER">{nameOf(event.organizer)}</FieldRow> : null}
        {event.recurring_event_id ? <FieldRow label="REPEATS">part of a series</FieldRow> : null}

        {attendees.length ? (
          <>
            <SectionHeading note={`${attendees.length} PEOPLE`}>ATTENDEES</SectionHeading>
            {attendees.map((person) => {
              const rsvp = rsvpOf(person);
              return (
                <div key={nameOf(person) + (person.email ?? '')} style={attendeeRow}>
                  <span style={{ ...markCell, color: rsvp.colour }}>{rsvp.mark}</span>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
                      color: rsvp.dim ? 'var(--bujo-text-dim)' : 'var(--bujo-text-primary)',
                    }}
                  >
                    {nameOf(person)}
                    {person.optional ? ' (optional)' : ''}
                  </span>
                  <span style={{ ...meta, flex: 'none' }}>{rsvp.label}</span>
                </div>
              );
            })}
            {/* The service truncates the list. Saying so is R13. */}
            {event.attendees_omitted ? (
              <div style={{ ...meta, paddingTop: 'var(--bujo-space-2)' }}>
                more attendees than the service returns · list truncated
              </div>
            ) : null}
          </>
        ) : null}

        <div style={note}>
          <span style={{ color: 'var(--bujo-accent)' }}>⌁</span> Read from{' '}
          <span style={{ color: 'var(--bujo-text-primary)' }}>{event.calendar_name}</span>
          {asOf ? (
            <>
              , as of <span style={{ color: 'var(--bujo-text-primary)' }}>{clock(asOf)}</span>
            </>
          ) : null}
          {freshness && freshness !== 'current' && freshness !== 'fresh' ? ` · ${freshness.toUpperCase()}` : ''}
          . BuJo does not own calendar data — there is no RSVP, edit or delete here. Change it
          where it lives.
        </div>
      </div>
    </div>
  );
}

/** `09:30` in the journal's own rendering of the instant. */
function clock(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

const sheet = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 36,
  background: 'var(--bujo-surface-page)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--bujo-space-3)',
  padding: 'var(--bujo-gutter)',
  paddingTop: 'calc(var(--bujo-gutter) + env(safe-area-inset-top))',
  paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
  paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
};
const eyebrow = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
};
const closeButton = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
const title = {
  margin: 0,
  paddingTop: 'var(--bujo-space-3)',
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.25 var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
};
const meta = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1 var(--bujo-font-mono)',
  letterSpacing: '0.06em',
  color: 'var(--bujo-text-meta)',
};
const attendeeRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--bujo-space-3)',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const markCell = {
  width: 14,
  flex: 'none' as const,
  textAlign: 'center' as const,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
};
const note = {
  paddingTop: 'var(--bujo-space-5)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.7 var(--bujo-font-sans)',
  color: 'var(--bujo-text-dim)',
};
